/**
 * The export dialog's PDF preview: the first page for the first selected
 * floor, laid out exactly as it will print (template, scale, text heights,
 * framing), with a Reframe button that hands the plan view to the framing
 * overlay. Uses the lazily loaded preview engine, like the template previews.
 */

import { ArrowUturnLeftIcon, ViewfinderCircleIcon } from '@heroicons/react/24/outline';
import { memo, useMemo } from 'react';
import {
  SHEET_DOC_SCHEMA_VERSION,
  defaultViewportSettings,
  expandPages,
  pageViewports,
  viewportShowsPlan,
  viewportSettingsKey,
  type PrintFloorInfo,
  type PrintTemplateSpec,
  type SheetDoc,
  type ViewportElement,
  type ViewportSettings,
} from 'shared-types';
import { EarthLoader } from '../../components/loaders/EarthLoader';
import { T } from '../../translation/src';
import { PanZoomStage } from '../sheets/PanZoomStage';
import { planPoints, type PlanPoint } from '../sheets/planFit';
import { useSheetSources } from '../sheets/useSheetContext';
import { usePlanContent } from './content/usePlanContent';
import { quickViewports, usePrintTemplates, type PrintTemplateChoice, type QuickPdfOptions } from './export/useQuickPdfExport';
import { orgPrintFontsOf } from './fonts/orgFonts';
import { PLAN_FONT } from './planFont';
import { pageLinesSvg, type PageLines } from './preview/pageLines';
import { usePreviewEnv } from './preview/usePreviewEnv';
import { findElement, worldCorners } from '../templateEditor/document/specOps';
import { usePrintImages } from './preview/usePrintImages';

/** What the framing overlay needs to reframe the preview's plan view. */
export interface QuickReframeRequest {
  element: ViewportElement;
  settings: ViewportSettings;
  floorId: string;
  fitPoints: PlanPoint[];
  /** The page's linework around the viewport, shown while framing. */
  pageLines: PageLines | null;
}

/** Exporting through a sheet: its pinned template and its document. */
export interface QuickPreviewSheet {
  name: string;
  number?: string | null;
  spec: PrintTemplateSpec;
  assets: { id: string; url: string }[];
  doc: SheetDoc;
}

interface QuickPdfPreviewProps {
  template: PrintTemplateChoice | null;
  /** When set, the preview is this sheet's page (its settings decide; no reframing here). */
  sheet?: QuickPreviewSheet | null;
  floor: { id: string; name: string } | null;
  options: Pick<QuickPdfOptions, 'scale' | 'textSizes' | 'framing'>;
  onReframe: (request: QuickReframeRequest) => void;
  onResetFraming: () => void;
}

const QuickPdfPreview_: React.FC<QuickPdfPreviewProps> = ({ template, sheet: viaSheet, floor, options, onReframe, onResetFraming }) => {
  const { library } = usePrintTemplates();
  const env = usePreviewEnv(useMemo(() => orgPrintFontsOf(library?.config.fonts), [library?.config.fonts]));
  const images = usePrintImages(library?.config.logo?.url, viaSheet ? viaSheet.assets : (template?.assets ?? []));
  const planEntities = usePlanContent();
  const sources = useSheetSources(library, viaSheet ? { name: viaSheet.name, number: viaSheet.number } : template ? { name: template.name } : null);
  const spec = viaSheet ? viaSheet.spec : template?.spec;

  const preview = useMemo(() => {
    if (!env || !spec || !template || !floor) return null;
    const floors: PrintFloorInfo[] = [{ id: floor.id, name: floor.name, elevation: 0 }];
    let sheet: SheetDoc;
    let viewports: SheetDoc['viewports'];
    if (viaSheet) {
      // The sheet as it exports, on this floor.
      sheet = { ...viaSheet.doc, floorIds: [floor.id] };
      viewports = sheet.viewports;
    } else {
      const base: SheetDoc = {
        schemaVersion: SHEET_DOC_SCHEMA_VERSION,
        templateRef: { source: template.source, id: template.id, version: template.version },
        fieldValues: {},
        revisions: [],
        floorIds: [floor.id],
        viewports: {},
        hiddenPages: [],
      };
      viewports = quickViewports(spec, base, floors, options, planEntities);
      sheet = { ...base, viewports };
    }
    const capHeightRatio = env.fonts.resolve(PLAN_FONT, 'normal', false).capHeightRatio;
    const [page] = env.engine.layoutDocument({
      spec,
      sheet,
      floors,
      sources,
      date: new Date(),
      missing: 'marker',
      fonts: env.fonts,
      image: (source) => images?.resolve(source) ?? null,
      viewportContent: (_instance, viewport) => {
        const { settings, element } = viewport;
        if (!viewportShowsPlan(settings) || !settings.floorId) return null;
        const { entities, blocks } = planEntities(settings.floorId, settings, capHeightRatio);
        return env.engine.planToDisplayItems({ entities, blocks, settings, widthMm: element.wMm, heightMm: element.hMm, fonts: env.fonts, font: PLAN_FONT });
      },
    });
    if (!page) return null;
    // The plan view reframing acts on: the first plan viewport of the first page.
    const instance = expandPages(spec, sheet, floors)[0];
    const element = instance ? pageViewports(instance.page).find((v) => v.allowedKinds.includes('PLAN_VECTOR')) : undefined;
    // A floor without a drawing has no centered settings: frame from the template default.
    const settings = element && instance ? (viewports[viewportSettingsKey(instance.key, element.id)] ?? { ...defaultViewportSettings('PLAN_VECTOR', element.defaultScaleDen), floorId: floor.id }) : undefined;
    const svg = env.engine.renderSvg(page.page, { font: env.svgFont, imageHref: (key) => images?.href(key) ?? null, idPrefix: 'qp', background: '#ffffff' });
    // The same page without the plan: its lines go over the view while framing.
    const [bare] = env.engine.layoutDocument({ spec, sheet, floors, sources, date: new Date(), missing: 'empty', fonts: env.fonts, image: () => null, viewportContent: () => null });
    const location = element ? findElement(spec, element.id) : null;
    const corners = location ? worldCorners(location) : [];
    const pageLines: PageLines | null =
      bare && corners.length > 0
        ? { svg: pageLinesSvg(env, bare.page), pageWidthMm: bare.page.widthMm, pageHeightMm: bare.page.heightMm, viewportXmm: Math.min(...corners.map((c) => c.x)), viewportYmm: Math.min(...corners.map((c) => c.y)) }
        : null;
    return { svg, aspect: page.page.widthMm / page.page.heightMm, element, settings, pageLines };
  }, [env, spec, template, viaSheet, floor, options, planEntities, sources, images]);

  if (!template || !floor) return null;
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
      {/* Wheel to zoom, drag to pan: check the framing up close before exporting. */}
      <div
        className="relative max-h-[60vh] w-full min-w-0 overflow-hidden sm:w-auto sm:flex-1 rounded-lg bg-gray-100 dark:bg-black/30"
        // The box has the paper's proportions (A-series √2, landscape or portrait), so the page fills it.
        style={{ aspectRatio: preview?.aspect ?? Math.SQRT2 }}
      >
        {preview ? (
          <PanZoomStage key={`${template.source}:${template.id}`} paddingClassName="p-1.5" compact>
            <div className="h-full max-w-full shadow [&>svg]:h-full [&>svg]:w-full" style={{ aspectRatio: preview.aspect }} dangerouslySetInnerHTML={{ __html: preview.svg }} />
          </PanZoomStage>
        ) : (
          <div className="flex h-full items-center justify-center">
            <EarthLoader />
          </div>
        )}
      </div>
      <div className="flex w-full flex-col justify-center gap-2 text-xs sm:w-40">
        <span className="text-gray-500 dark:text-gray-400">
          {floor.name}
          {!viaSheet && options.framing ? (
            <>
              {' · '}
              <T _str="framed by hand" />
            </>
          ) : null}
        </span>
        {viaSheet ? (
          <span className="text-gray-500 dark:text-gray-400">
            <T _str="framing, layers and pens come from the sheet" swc />
          </span>
        ) : (
          <button
            type="button"
            disabled={!preview?.element || !preview.settings}
            onClick={() => {
              if (!preview?.element || !preview.settings) return;
              onReframe({ element: preview.element, settings: preview.settings, floorId: floor.id, fitPoints: planPoints(planEntities(floor.id, preview.settings, 0.7).entities), pageLines: preview.pageLines });
            }}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 font-medium hover:border-french-violet-600 hover:text-french-violet-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:hover:border-neon-green-300 dark:hover:text-neon-green-300"
          >
            <ViewfinderCircleIcon className="h-4 w-4" />
            <T _str="reframe" swc />
          </button>
        )}
        {!viaSheet && options.framing && (
          <button type="button" onClick={onResetFraming} className="flex items-center justify-center gap-1 text-french-violet-600 hover:underline dark:text-neon-green-300">
            <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
            <T _str="center on each floor again" />
          </button>
        )}
      </div>
    </div>
  );
};

export const QuickPdfPreview = memo(QuickPdfPreview_);
